/**
 * Shared API client utilities for @landtrustinc/buck-chat
 * Provides common error handling, types, and fetch wrapper
 */

// Base error types shared across all API clients
export enum ApiErrorType {
  RATE_LIMIT = 'rate_limit',
  NETWORK_ERROR = 'network_error',
  UNAUTHORIZED = 'unauthorized',
  NOT_FOUND = 'not_found',
  VALIDATION_ERROR = 'validation_error',
  CONFLICT = 'conflict',
  UNKNOWN = 'unknown',
}

// Base API error class that specific APIs can extend
export class ApiError extends Error {
  type: ApiErrorType;
  statusCode?: number;
  cooldownSeconds?: number;

  constructor(
    message: string,
    type: ApiErrorType,
    options?: { statusCode?: number; cooldownSeconds?: number }
  ) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.statusCode = options?.statusCode;
    this.cooldownSeconds = options?.cooldownSeconds;
  }
}

/**
 * Parse HTTP response into ApiError
 * Maps common HTTP status codes to error types
 */
export const parseApiError = async (response: Response): Promise<ApiError> => {
  let message = 'An error occurred';
  let cooldownSeconds: number | undefined;

  try {
    const errorBody = await response.json();
    message = errorBody.message || errorBody.error || message;

    // Extract cooldown from rate limit responses
    if (response.status === 429) {
      const match = message.match(/(\d+)\s*seconds?/i);
      cooldownSeconds = match ? parseInt(match[1], 10) : 60;
    }
  } catch {
    // If we can't parse JSON, use status text
    message = response.statusText || `Request failed with status ${response.status}`;
  }

  // Map HTTP status codes to error types
  switch (response.status) {
    case 429:
      return new ApiError(
        cooldownSeconds ? `Please wait ${cooldownSeconds} seconds before trying again` : message,
        ApiErrorType.RATE_LIMIT,
        { statusCode: response.status, cooldownSeconds }
      );
    case 401:
      return new ApiError(message || 'Authentication required', ApiErrorType.UNAUTHORIZED, {
        statusCode: response.status,
      });
    case 404:
      return new ApiError(message || 'Resource not found', ApiErrorType.NOT_FOUND, {
        statusCode: response.status,
      });
    case 400:
      return new ApiError(message || 'Invalid request', ApiErrorType.VALIDATION_ERROR, {
        statusCode: response.status,
      });
    case 409:
      return new ApiError(message || 'Resource already exists', ApiErrorType.CONFLICT, {
        statusCode: response.status,
      });
    default:
      return new ApiError(message, ApiErrorType.UNKNOWN, { statusCode: response.status });
  }
};

/**
 * Handle network errors and wrap unknown errors
 */
export const handleNetworkError = (error: unknown): ApiError => {
  // Already an ApiError, return as-is
  if (error instanceof ApiError) {
    return error;
  }

  // Network/fetch failure
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new ApiError(
      'Network error. Please check your connection and try again.',
      ApiErrorType.NETWORK_ERROR
    );
  }

  // Other errors
  return new ApiError(
    error instanceof Error ? error.message : 'An unexpected error occurred',
    ApiErrorType.UNKNOWN
  );
};

/**
 * Typed fetch wrapper with credentials and JSON handling
 * Automatically includes credentials and Content-Type header
 */
export const fetchWithCredentials = async <T>(url: string, options?: RequestInit): Promise<T> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw await parseApiError(response);
    }

    // Handle empty responses (204 No Content)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return undefined as T;
    }

    return response.json();
  } catch (error) {
    throw handleNetworkError(error);
  }
};
